import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useListboxNavigation } from './useListboxNavigation';

/**
 * The state machine behind every "type a few letters, pick a medicine" field in
 * the app: open/closed, the highlighted option, outside-click, the keyboard, and
 * the ARIA wiring that makes an input plus a `<ul>` a combobox rather than two
 * unrelated elements.
 *
 * It was written five times before this existed — the analytics medicine filter,
 * the consult autosuggest, and the item pickers on Issue, Adjust and Add Stock —
 * and the copies had drifted apart in exactly the way copies do. Three of the
 * five had no keyboard navigation and no combobox roles at all, so the results
 * menu could only be used with a mouse and a screen reader was never told it had
 * appeared. One of them had lost outside-click-to-close. The two that were right
 * were the two most recent, which is the tell: the pattern was being re-derived
 * rather than reused.
 *
 * What this owns is everything those five agreed on. What it deliberately does
 * NOT own is the input's markup and the option rows — the five look different on
 * purpose (a plain `Input` inside a `Field`, versus a bordered shell with a
 * search icon) and a shared component that unified those would be a redesign
 * wearing a refactor's clothes. Callers spread `inputProps` onto whatever input
 * they already render.
 *
 * Not folded in: `ChipInputAutosuggest`. It is the same machine plus chips and a
 * Backspace-removes-the-last-one rule, and giving this hook an escape hatch for
 * that would cost more than the copy does.
 */

/** Text state is always the caller's. */
export interface UseComboboxParams<T> {
  /**
   * The input's text. Owned by the caller, always — most call sites hold it in
   * their own `useState`, but Add Stock's brand field is a form value that other
   * fields read, and a hook that owned the text internally would have needed a
   * second mode to support it.
   */
  text: string;
  onTextChange: (text: string) => void;
  /**
   * The options as rendered, in order. Callers that show a synthetic first row
   * (the consult field's "Add ⟨typed text⟩") prepend it here rather than
   * offsetting indices by hand, which is where the old copies got fiddly.
   */
  options: readonly T[];
  /**
   * Identity of an option, used to notice that the option set changed and
   * re-default the highlight. A stale highlight index pointing at a different
   * medicine is the failure this prevents.
   */
  optionKey: (option: T, index: number) => string;
  onSelect: (option: T) => void;
  /**
   * Rows that render but cannot be chosen — an out-of-stock medicine on the
   * Issue and Adjust pages. The highlight still moves over them, matching what
   * a disabled button does to a mouse: it can be pointed at and does nothing.
   */
  isOptionDisabled?: (option: T) => boolean;
  /** Characters of trimmed text before the menu may open. */
  minChars?: number;
  /**
   * Whether the menu is worth opening with no options — because the caller
   * renders a "searching…" or "no matches" row. Menus with nothing to say when
   * empty stay shut instead of flashing an empty box.
   */
  showsEmptyState?: boolean;
  /** Option highlighted on open and whenever the option set changes. */
  defaultIndex?: number;
  /** Names the listbox for assistive technology. */
  ariaLabel: string;
}

export interface Combobox<T> {
  /** Wrap the input in this — it is half of the outside-click test. */
  rootRef: React.RefObject<HTMLDivElement>;
  /** The element the menu is positioned under and width-matched to. */
  anchorRef: React.RefObject<HTMLDivElement>;
  /** True when the menu should be on screen. */
  isOpen: boolean;
  /** `text`, trimmed — what the caller should send to its query hook. */
  query: string;
  activeIndex: number;
  /** Spread onto the `<input>`; carries every combobox aria attribute. */
  inputProps: {
    role: 'combobox';
    'aria-expanded': boolean;
    'aria-controls': string;
    'aria-autocomplete': 'list';
    'aria-activedescendant': string | undefined;
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onFocus: () => void;
    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  };
  /** Spread onto `<ComboboxListbox>`. */
  listboxProps: {
    anchorRef: React.RefObject<HTMLDivElement>;
    panelRef: React.MutableRefObject<HTMLDivElement | null>;
    open: boolean;
    id: string;
    ariaLabel: string;
    options: readonly T[];
    activeIndex: number;
    optionDomId: (index: number) => string;
    optionKey: (option: T, index: number) => string;
    isOptionDisabled: (option: T) => boolean;
    onHover: (index: number) => void;
    onPick: (option: T) => void;
  };
  /** Shut the menu — for a caller that selected through some other affordance. */
  close: () => void;
}

export function useCombobox<T>({
  text,
  onTextChange,
  options,
  optionKey,
  onSelect,
  isOptionDisabled,
  minChars = 0,
  showsEmptyState = false,
  defaultIndex = 0,
  ariaLabel,
}: UseComboboxParams<T>): Combobox<T> {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const listId = useId();

  const query = text.trim();
  const isOpen = open && query.length >= minChars && (options.length > 0 || showsEmptyState);

  const optionDomId = useCallback((i: number) => `${listId}-opt-${i}`, [listId]);
  const signature = options.map(optionKey).join('|');
  const nav = useListboxNavigation({
    count: options.length,
    open: isOpen,
    signature,
    defaultIndex,
    optionDomId,
  });

  useEffect(() => {
    if (!open) return;
    function onDocPointer(e: MouseEvent) {
      const target = e.target as Node;
      // The menu is portaled out of `rootRef`'s subtree, so it has to be checked
      // separately — otherwise clicking an option closes the menu before the
      // click lands and nothing is selected. The test is against the panel
      // rather than the list inside it, because the panel's scrollbar and border
      // are part of the menu too, and dragging a scrollbar must not dismiss it.
      if (rootRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', onDocPointer);
    return () => document.removeEventListener('mousedown', onDocPointer);
  }, [open]);

  const disabled = useCallback(
    (option: T) => isOptionDisabled?.(option) ?? false,
    [isOptionDisabled],
  );

  const pick = useCallback(
    (option: T) => {
      if (disabled(option)) return;
      onSelect(option);
      setOpen(false);
    },
    [disabled, onSelect],
  );

  return {
    rootRef,
    anchorRef,
    isOpen,
    query,
    activeIndex: nav.activeIndex,
    inputProps: {
      role: 'combobox',
      'aria-expanded': isOpen,
      'aria-controls': listId,
      'aria-autocomplete': 'list',
      'aria-activedescendant': nav.activeDescendantId,
      value: text,
      onChange: (e) => {
        onTextChange(e.target.value);
        setOpen(true);
      },
      onFocus: () => setOpen(true),
      onKeyDown: (e) => {
        if (nav.onArrowKeys(e)) return;
        if (e.key === 'Escape') {
          setOpen(false);
        } else if (e.key === 'Enter') {
          // Prevented whether or not something is highlighted: this input is
          // usually inside a form, and letting Enter through would submit it
          // while the user was still choosing.
          e.preventDefault();
          const option = options[nav.activeIndex];
          if (option !== undefined) pick(option);
        }
      },
    },
    listboxProps: {
      anchorRef,
      panelRef,
      open: isOpen,
      id: listId,
      ariaLabel,
      options,
      activeIndex: nav.activeIndex,
      optionDomId,
      optionKey,
      isOptionDisabled: disabled,
      onHover: nav.setActiveIndex,
      onPick: pick,
    },
    close: () => setOpen(false),
  };
}
